// Mobile detection and performance utilities
// Only activates optimizations for mobile devices (max-width: 1024px)

export const MOBILE_BREAKPOINT = 1024;

interface NetworkInformation extends EventTarget {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  saveData?: boolean;
  downlink?: number;
  rtt?: number;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
};

export const isSlowConnection = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  
  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  
  if (!connection) return false;
  
  // Save-Data mode enabled
  if (connection.saveData) return true;
  
  // Slow network types
  if (connection.effectiveType && ['2g', 'slow-2g', '3g'].includes(connection.effectiveType)) {
    return true;
  }
  
  // High latency
  if (connection.rtt && connection.rtt > 400) return true;
  
  return false;
};

export const shouldOptimizeForMobile = (): boolean => {
  return isMobileDevice() || isSlowConnection();
};

export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Preload critical resources only on mobile
export const preloadCriticalAsset = (href: string, as: 'image' | 'video' | 'font' | 'style') => {
  if (!shouldOptimizeForMobile()) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = href;
  if (as === 'font') {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
};

// Dynamically import component only when needed
export const lazyLoadComponent = async <T>(
  importFn: () => Promise<{ default: T }>,
  fallback?: T
): Promise<T> => {
  try {
    const module = await importFn();
    return module.default;
  } catch (error) {
    console.error('Failed to lazy load component:', error);
    if (fallback) return fallback;
    throw error;
  }
};

// Get optimal image width based on container and device
export const getOptimalImageWidth = (containerWidth: number): number => {
  if (!isMobileDevice()) return containerWidth;
  
  // For mobile, limit to actual device width to avoid over-fetching
  const deviceWidth = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;
  
  // Cap at 2x for mobile to balance quality and performance
  const maxDpr = Math.min(dpr, 2);
  
  return Math.min(containerWidth * maxDpr, deviceWidth * maxDpr);
};

// Generate srcset for responsive images
export const generateSrcSet = (
  basePath: string,
  widths: number[],
  format: 'webp' | 'avif' | 'jpg' = 'webp'
): string => {
  return widths
    .map(width => `${basePath}-${width}w.${format} ${width}w`)
    .join(', ');
};
